import { FEATURE_FLAGS_COLLECTION, type FeatureFlag, isFeatureFlag, resolveFlag } from '@parsleyai/feature-flags'
import { collection, type DocumentData, onSnapshot, type QuerySnapshot } from 'firebase/firestore'
import { createContext, type ReactNode, useCallback, useContext, useState } from 'react'
import { db } from '@/services/firebase/config'
import { useAuth } from '@/store/auth/useAuth'
import { useLiveSubscription } from '@/store/userData/useLiveSubscription'

interface FeatureFlagsContextValue {
  isEnabled: (flag: FeatureFlag) => boolean
}

// Unlike the app's other contexts, a missing provider resolves to the registry defaults instead of
// throwing. Every other unreadable case already falls back that way, and a flag is a safety
// mechanism — one checked from somewhere outside the provider should quietly read its default, not
// take the page down. It also keeps flags invisible to tests that render a page for other reasons.
const FALLBACK: FeatureFlagsContextValue = { isEnabled: (flag) => resolveFlag(flag, undefined) }

const FeatureFlagsContext = createContext<FeatureFlagsContextValue>(FALLBACK)

/**
 * Live feature-flag state, shared app-wide. Flags are global rather than per-user, so this listens
 * on the collection once rather than hanging off a uid — but it still waits for auth, because the
 * rule only grants a signed-in read.
 *
 * The collection is sparse: a document exists only for a flag an admin has actually set, and every
 * unresolved case — no document, a malformed one, a listener that hasn't landed yet, a read that
 * failed — falls back to the registry default. A flag that can't be read must never break a page.
 */
export function FeatureFlagsProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth()
  const [stored, setStored] = useState<Record<string, Record<string, unknown>>>({})

  useLiveSubscription<QuerySnapshot<DocumentData>>({
    // Global, not uid-scoped — the key only gates the listener on being signed in.
    subscriptionKey: isAuthenticated ? FEATURE_FLAGS_COLLECTION : null,
    label: 'featureFlags',
    subscribe: (_key, { onData, onError }) => onSnapshot(collection(db, FEATURE_FLAGS_COLLECTION), onData, onError),
    onData: (snapshot) => {
      const next: Record<string, Record<string, unknown>> = {}
      for (const docSnap of snapshot.docs) {
        // An unregistered document is ignored rather than trusted — a stale flag left behind after
        // its key was retired shouldn't resurrect as anything.
        if (isFeatureFlag(docSnap.id)) next[docSnap.id] = docSnap.data()
      }
      setStored(next)
    }
  })

  const isEnabled = useCallback((flag: FeatureFlag) => resolveFlag(flag, stored[flag]), [stored])

  return <FeatureFlagsContext.Provider value={{ isEnabled }}>{children}</FeatureFlagsContext.Provider>
}

export function useFeatureFlags(): FeatureFlagsContextValue {
  return useContext(FeatureFlagsContext)
}
